import React, { createContext, useContext, useState, useEffect } from "react";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { decryptField } from "../utils/encryption";

const PreferencesContext = createContext({
  accountantEmail: "",
  deliveryMethod: "manual",
  companyName: "",
  companyAddress: "",
  companyNumber: "",
  preferencesLoaded: false,
  plan: "",
  logoUrl: "",
  bankDetails: "",
  bankConnected: false,
  accountingBasis: "accrual", // 'accrual' or 'cash'
  setAccountantEmail: () => {},
  setDeliveryMethod: () => {},
  setCompanyName: () => {},
  setCompanyAddress: () => {},
  setCompanyNumber: () => {},
  setAccountingBasis: () => {},
  refreshPreferences: () => {},
});

export const PreferencesProvider = ({ children }) => {
  const [accountantEmail, setAccountantEmail] = useState("");
  const [deliveryMethod, setDeliveryMethod] = useState("manual");
  const [companyName, setCompanyName] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [companyNumber, setCompanyNumber] = useState("");
  const [bankDetails, setBankDetails] = useState("");
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const [plan, setPlan] = useState("");
  const [bankConnected, setBankConnected] = useState(false);
  const [accountingBasis, setAccountingBasis] = useState("accrual");

  const refreshPreferences = async () => {
    if (!auth.currentUser) return;

    try {
      const ref = doc(db, "users", auth.currentUser.uid);
      const snap = await getDoc(ref);
      const data = snap.data();
      const prefs = data?.preferences || {};

      try {
        setAccountantEmail(decryptField(prefs.accountantEmail || ""));
      } catch {
        setAccountantEmail("");
      }

      setDeliveryMethod(prefs.deliveryMethod || "manual");
      setCompanyName(prefs.companyName || "");

      try {
        setCompanyAddress(decryptField(prefs.companyAddress || ""));
      } catch {
        setCompanyAddress("");
      }

      try {
        setCompanyNumber(decryptField(prefs.companyNumber || ""));
      } catch {
        setCompanyNumber("");
      }

      try {
        setBankDetails(decryptField(prefs.bankDetails || ""));
      } catch {
        setBankDetails("");
      }

      setLogoUrl(prefs.logoUrl || "");
      setAccountingBasis(prefs.accountingBasis || "accrual");
      setPlan(data?.plan || "");

      const connection = data?.bankConnection;
      setBankConnected(
        connection?.connected === true &&
          Array.isArray(connection.accountIds) &&
          connection.accountIds.length > 0
      );

      setPreferencesLoaded(true);
    } catch (err) {
      console.error("Error loading preferences:", err);
    }
  };

  // ✅ Load on mount
  useEffect(() => {
    refreshPreferences();
  }, []);

  return (
    <PreferencesContext.Provider
      value={{
        accountantEmail,
        setAccountantEmail,
        deliveryMethod,
        setDeliveryMethod,
        companyName,
        setCompanyName,
        companyAddress,
        setCompanyAddress,
        companyNumber,
        setCompanyNumber,
        refreshPreferences,
        setBankDetails,
        bankDetails,
        preferencesLoaded,
        logoUrl,
        plan,
        bankConnected,
        accountingBasis,
        setAccountingBasis,
      }}
    >
      {children}
    </PreferencesContext.Provider>
  );
};

export const usePreferences = () => useContext(PreferencesContext);
